/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/formula-patch.ts
 * FILE_ID: QB-AGGRID-FORMULA-PATCH-001
 * PURPOSE: Pure I→O helpers that turn one raw cell-edit value into a
 *          `Partial<QuoteLineItem>` patch that the persistence layer
 *          can send to the backend. The whole point of this module
 *          is to make estimators' Excel-style `=qty * 1.1` typing
 *          work inside the AG-Grid scope sheet — without surprising
 *          the cells that aren't formulas (which keep their existing
 *          plain-number contract).
 *
 * WHY: Before this helper landed, ScopeAgGrid's handleCellEdit just
 *      patched `{[field]: rawValue}`. That worked for plain numbers
 *      but silently sent the string "=qty * 1.1" to a Decimal column
 *      — which the backend would either reject (400) or coerce to
 *      NaN. The legacy ScopeSheetGrid had per-cell formula handling
 *      (`evaluateCell` + `formulas` JSON merge); the AG-Grid path
 *      lost it during the rewrite. This file restores parity AND
 *      keeps the persist handler small (rule 1.7: 300-line cap).
 *
 * FLOW:
 *   ┌──────────────────┐   ┌──────────────────┐   ┌──────────────────┐
 *   │ AG-Grid          │ → │ buildFormulaCell │ → │ Partial<QuoteLine│
 *   │ onCellEdit value │   │ Patch(row,field, │   │ Item> patch with │
 *   │ (string|number)  │   │ raw)             │   │ [field] AND      │
 *   │                  │   │                  │   │ optional formulas│
 *   └──────────────────┘   └──────────────────┘   └──────────────────┘
 *
 * DEPENDENCIES:
 *   - QB-MATH-FORMULA-001 (formula.ts) — evaluateCell / isFormulaText
 *
 * RELATED:
 *   - QB-AGGRID-PERSIST-HANDLERS-001 — primary consumer
 *   - QB-AGGRID-FORMULA-BAR-HOOK-001 — formula bar uses the same patch
 *   - quotebuilder/models/line_item.py — `formulas` JSONField docstring
 */

import type { QuoteLineItem } from '@/lib/quoteApi'
import {
  evaluateCell,
  isFormulaText,
  type FormulaContext,
} from '@/lib/quote-builder-math/formula'

// =============================================================================
// ROW CONTEXT
// =============================================================================

/**
 * Numeric field names from `QuoteLineItem` that estimators reference
 * inside formulas. Kept here (NOT imported from the type def) so a
 * field rename in the API types fails loudly via TS narrowing at the
 * call site, rather than silently dropping the variable from the
 * evaluator context.
 */
const NUMERIC_LINE_ITEM_FIELDS = [
  // Quantity / productivity / pricing primaries
  'quantity',
  'productivity_pmpd',
  'adjusted_rate',
  // Labour derivatives
  'labour_days',
  'labour_hours',
  'labour_cost',
  'labour_chargeout',
  // Material + plant + subcon costs
  'material_cost',
  'material_chargeout',
  'plant_cost',
  'plant_chargeout',
  'plant_unit_cost',
  'plant_quantity',
  'subcon_cost',
  'subcon_chargeout',
  'subcon_quote',
  'subcon_days',
  // Computed roll-ups
  'rate_per_unit',
  'subtotal',
  'total_cost',
  'chargeout_total',
  'margin',
  'margin_percent',
  // Overrides
  'override_labour_cost',
  'override_material_cost',
  'override_chargeout',
] as const

/**
 * SAM-parity shorthand aliases. Estimators import quote sheets that
 * use `qty`, `pmpd`, `rate` instead of the full field names. Aliases
 * are evaluated by the formula engine's lowercase fallback as a
 * second chance, but we expose them explicitly so an upper-case
 * `QTY` also resolves via the engine's `key.toLowerCase()` lookup.
 *
 * Map shape: alias → real field name on QuoteLineItem.
 */
const FIELD_ALIASES: Readonly<Record<string, string>> = {
  qty: 'quantity',
  pmpd: 'productivity_pmpd',
  rate: 'adjusted_rate',
}

function asNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/**
 * Build the FormulaContext that gets fed into `evaluateCell` when
 * the user types `=...` in this row. Includes every numeric line-
 * item field plus a small set of SAM shorthand aliases.
 *
 * I→O. Pure. Returns a fresh object each call (callers must not
 * cache by row identity — fields may have been mutated).
 */
export function buildRowFormulaContext(
  row: QuoteLineItem,
): FormulaContext {
  const ctx: Record<string, number | null> = {}
  const indexable = row as unknown as Record<string, unknown>
  for (const key of NUMERIC_LINE_ITEM_FIELDS) {
    ctx[key] = asNumOrNull(indexable[key])
  }
  for (const [alias, target] of Object.entries(FIELD_ALIASES)) {
    ctx[alias] = asNumOrNull(indexable[target])
  }
  return ctx
}

// =============================================================================
// PATCH BUILDER
// =============================================================================

export type FormulaCellInput = string | number | null | undefined

export interface FormulaPatchOptions {
  /**
   * Caller asserts whether this column carries Decimal/Number values on
   * the backend (e.g. `quantity`, `adjusted_rate`, `labour_chargeout`).
   *
   * - true  → unparseable text becomes null (the Decimal column can't
   *           store 'N/A' anyway, so we drop it rather than corrupt
   *           the column with a string value that round-trips as NaN).
   * - false → unparseable text is preserved verbatim (text fields like
   *           `description`, `location`, `internal_notes` must accept
   *           any string).
   *
   * Formula detection (`=...`) runs regardless — but text columns will
   * almost never see a formula in practice; the persist hook still
   * routes through this helper for uniform handling.
   */
  isNumericField: boolean
}

/** Internal classification used to decide patch shape. */
type ParsedInput =
  | { kind: 'empty' }
  | { kind: 'literal-num'; value: number | null }
  | { kind: 'literal-text'; value: string }
  | { kind: 'formula'; expression: string; value: number; ok: true }
  | { kind: 'formula-error'; expression: string; ok: false }

function classify(
  raw: FormulaCellInput,
  ctx: FormulaContext,
  isNumericField: boolean,
): ParsedInput {
  if (raw === null || raw === undefined || raw === '') {
    return { kind: 'empty' }
  }
  if (typeof raw === 'number') {
    return {
      kind: 'literal-num',
      value: Number.isFinite(raw) ? raw : null,
    }
  }
  if (typeof raw !== 'string') {
    // Defensive: AG-Grid value pipelines can occasionally emit
    // booleans on toggle columns; treat as not-a-number so a Decimal
    // column gets null rather than 'true'.
    return { kind: 'literal-num', value: null }
  }
  const trimmed = raw.trim()
  if (trimmed === '') return { kind: 'empty' }

  if (isFormulaText(trimmed)) {
    const result = evaluateCell(trimmed, ctx)
    if (result.ok) {
      return {
        kind: 'formula',
        expression: trimmed,
        value: result.value,
        ok: true,
      }
    }
    return { kind: 'formula-error', expression: trimmed, ok: false }
  }

  const parsedNum = Number(trimmed)
  if (Number.isFinite(parsedNum)) {
    return { kind: 'literal-num', value: parsedNum }
  }

  // Unparseable text. For numeric columns, drop to null (avoid
  // corrupting a Decimal column). For text columns, preserve the
  // raw string so 'Updated wall' lands on `description` unchanged.
  if (isNumericField) {
    return { kind: 'literal-num', value: null }
  }
  return { kind: 'literal-text', value: trimmed }
}

/**
 * Compute the next `formulas` map for a single-field edit, OR return
 * `undefined` to signal "no change — don't include it in the patch".
 *
 * Cases:
 *   - User typed a formula AND the row already had the same expression
 *     for this field → no change.
 *   - User typed a formula AND the row had a different / no expression
 *     for this field → next map is the same map with the new expr.
 *   - User typed a non-formula AND the row had a formula for this field
 *     → next map is the same map with this key REMOVED.
 *   - User typed a non-formula AND the row had no formula for this
 *     field → no change.
 *
 * Returning the patched-but-still-empty `{}` is intentional when the
 * last formula is being cleared; the persist layer overwrites the
 * field server-side rather than treating `{}` as "no change".
 */
function nextFormulasMap(
  current: Record<string, string> | null | undefined,
  field: string,
  nextExpression: string | null,
): Record<string, string> | undefined {
  const currentMap: Record<string, string> = current ?? {}
  const currentExpr = currentMap[field] ?? null

  if (nextExpression === null) {
    if (currentExpr === null) return undefined
    // Drop the cleared field; preserve all others.
    const next: Record<string, string> = {}
    for (const [k, v] of Object.entries(currentMap)) {
      if (k !== field) next[k] = v
    }
    return next
  }

  if (currentExpr === nextExpression) return undefined
  return { ...currentMap, [field]: nextExpression }
}

export interface FormulaPatchResult {
  /** The patch to send via onUpdateItem. */
  patch: Partial<QuoteLineItem>
  /**
   * True iff the user's input was `=...` AND the formula could be
   * parsed + evaluated. False for plain numbers, empty, or
   * unparseable formulas. Consumers can use this for telemetry /
   * cell badges; it does NOT change the patch shape.
   */
  evaluatedFormula: boolean
  /**
   * True iff the user typed `=...` BUT the engine returned an error
   * (syntax / div-by-zero / unknown name). The expression is still
   * preserved on the row so the user can fix it; the numeric field
   * is set to null so the math fallback shows '—' instead of the
   * stale prior value.
   */
  formulaError: boolean
}

/**
 * Convert one cell edit into a backend patch that handles formulas.
 *
 * I→O. Pure. Never mutates `row`. Reads the current `formulas` map
 * off `row` to compute the minimal diff (omits `formulas` from the
 * patch when nothing changed).
 *
 * Use this from EVERY single-cell edit entry point — handleCellEdit,
 * handlePadPromote, the formula bar commit, fill-down (when fill
 * source carries a formula). Centralising the contract means a future
 * change to formula semantics propagates everywhere automatically.
 */
export function buildFormulaCellPatch(
  row: QuoteLineItem,
  field: string,
  rawInput: FormulaCellInput,
  options: FormulaPatchOptions,
): FormulaPatchResult {
  const ctx = buildRowFormulaContext(row)
  const parsed = classify(rawInput, ctx, options.isNumericField)

  const patch: Partial<QuoteLineItem> = {}

  switch (parsed.kind) {
    case 'empty': {
      ;(patch as Record<string, unknown>)[field] = null
      const nextFormulas = nextFormulasMap(row.formulas, field, null)
      if (nextFormulas !== undefined) patch.formulas = nextFormulas
      return { patch, evaluatedFormula: false, formulaError: false }
    }
    case 'literal-num': {
      ;(patch as Record<string, unknown>)[field] = parsed.value
      const nextFormulas = nextFormulasMap(row.formulas, field, null)
      if (nextFormulas !== undefined) patch.formulas = nextFormulas
      return { patch, evaluatedFormula: false, formulaError: false }
    }
    case 'literal-text': {
      // Text column edits (description, location, ...). Formulas don't
      // apply here; never touch the formulas map.
      ;(patch as Record<string, unknown>)[field] = parsed.value
      return { patch, evaluatedFormula: false, formulaError: false }
    }
    case 'formula': {
      ;(patch as Record<string, unknown>)[field] = parsed.value
      const nextFormulas = nextFormulasMap(row.formulas, field, parsed.expression)
      if (nextFormulas !== undefined) patch.formulas = nextFormulas
      return { patch, evaluatedFormula: true, formulaError: false }
    }
    case 'formula-error': {
      // Preserve the bad expression so the user can fix it; null out
      // the numeric column so the row's roll-up math doesn't keep
      // using a stale value that no longer matches the typed expr.
      ;(patch as Record<string, unknown>)[field] = null
      const nextFormulas = nextFormulasMap(row.formulas, field, parsed.expression)
      if (nextFormulas !== undefined) patch.formulas = nextFormulas
      return { patch, evaluatedFormula: false, formulaError: true }
    }
  }
}
